import React, { useState, useEffect, useContext } from 'react'
import { ThemeContext } from '../../context/ThemeContext';
import { Delete } from 'lucide-react';

import CustomText from '../../styles/CustomText';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { checkQssPassword, checkHeaderText } from '../../store';
import useQssMnemonic from '../../hooks/useQssMnemonic';
import useSecureStorage from '../../hooks/useSecureStorage';

/**
 * @brief 핀코드 입력
 * @returns 
 */
const QssLoadPassword: React.FC = () => {
	const { theme } = useContext(ThemeContext)
	const { t } = useTranslation();
	const navigate = useNavigate();
	let dispatch = useDispatch();
	const { chackQssPassword } = useQssMnemonic()
	const { getBlockChain } = useSecureStorage()

	const [pin, setPin] = useState<string[]>([])
	const [confirmPin, setConfirmPin] = useState<string[]>([])
	const [isConfirming, setIsConfirming] = useState(false)
	const [error, setError] = useState(false)

	const [shuffledKeys, setShuffledKeys] = useState<string[]>([])

	useEffect(() => {
		dispatch(checkHeaderText({ "link": "QssLoadPassword", "title": '' }))
		setShuffledKeys(shuffleArray(['1', '2', '3', '4', '5', '6', '7', '8', '9', '0']))
	}, [])

	useEffect(() => {
		if (pin.length === 6 && !isConfirming) {
			setTimeout(() => {
				handleConfirm()
			}, 200)
		}
	}, [pin, isConfirming])


	const handleKeyPress = (key: string) => {
		if (key === 'backspace') {
			setPin(prevPin => prevPin.slice(0, -1))
		} else {
			if (pin.length < 6) {
				setPin(prevPin => [...prevPin, key])
			}
		}
	}

	const handleConfirm = async () => {
		try {
			const strPin = pin.join('')
			const selNetwork = await getBlockChain()
			const checkPin = await chackQssPassword(strPin, selNetwork)

			if (true === checkPin) {
				// qSS 비밀번호 임시 저장
				dispatch(checkQssPassword(strPin))

				// QssLoadProcess 페이지로 이동
				setTimeout(() => {
					// @ts-ignore
					navigate(`/QssLoadProcess`, { replace: true, state: { } });
					setError(false)
				}, 1000)

			} else {
				setError(true)
				setTimeout(() => {
					setPin([])
					setError(false)
				}, 1000)
			}
		} catch (error) {
			console.error('Failed to retrieve the pin from storage', error)
			setError(true)
			setTimeout(() => {
				setPin([])
				setError(false)
			}, 1000)
		}
	}

	const shuffleArray = (array: string[]) => {
		let currentIndex = array.length,
			randomIndex

		while (currentIndex !== 0) {
			randomIndex = Math.floor(Math.random() * currentIndex)
			currentIndex--
				;[array[currentIndex], array[randomIndex]] = [array[randomIndex], array[currentIndex]]
		}

		return array
	}

	const renderKey = (key: string, index: number) => (
		<div key={index}>
			<div
				onClick={() => { handleKeyPress(key) }}
				style={{
					borderRadius: '50%',
				}}
			>
				<div style={styles.key}>
					<CustomText weight='medium' style={{ ...styles.keyText, color: theme.label_strong }}>
						{key}
					</CustomText>
				</div >
			</div >
		</div >
	)

	const styles = {
		container: {
			padding: "60px 0px 24px",
		} as React.CSSProperties,
		header: {
			height: "135px"
		} as React.CSSProperties,
		title1: {
			fontSize: 24,
			textAlign: 'center',
		} as React.CSSProperties,
		title2: {
			fontSize: 24,
			textAlign: 'center',
			marginBottom: 10
		} as React.CSSProperties,
		subtitle1: {
			fontSize: 14,
			textAlign: 'center'
		} as React.CSSProperties,
		pinContainer: {
			display: "flex",
			justifyContent: 'center',
			alignItems: 'center',
			gap: '10px',
		} as React.CSSProperties,
		pinDot: {
			width: 16,
			height: 16,
			borderRadius: 8,
			margin: 8
		} as React.CSSProperties,
		pinDotError: {
			backgroundColor: theme.status_destrective,
		} as React.CSSProperties,
		basicText: {
			fontSize: 14,
			height: "40px",
			display: "flex",
			alignItems: "flex-start",
			justifyContent: "center",
			marginTop: "15px",
			textAlign: 'center',
			lineHeight: "22px"
		} as React.CSSProperties,
		errorText: {
			fontSize: 14,
			height: "40px",
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			marginTop: "15px"
		} as React.CSSProperties,
		keypadContainer: {
			width: '100%',
		} as React.CSSProperties,
		keypadRow: {
			display: "flex",
			justifyContent: 'space-around',
			width: '100%',
			marginBottom: "5px"
		} as React.CSSProperties,
		key: {
			display: "flex",
			width: 70,
			height: 70,
			justifyContent: 'center',
			alignItems: 'center',
			cursor: "pointer"
		} as React.CSSProperties,
		keyPlaceholder: {
			width: 70,
			height: 70,
		} as React.CSSProperties,
		keyText: {
			fontSize: 24,
		} as React.CSSProperties,
	}

	return (
		<div className='container' style={{ ...styles.container, backgroundColor: theme.background_normal }}>
			<div style={styles.header}>
				<CustomText weight="medium" style={{ ...styles.title1, color: theme.label_strong }}>
					{t('qss_load_password_screen_message_01')}
				</CustomText>
				<CustomText weight="medium" style={{ ...styles.title2, color: theme.label_strong }}>
					{t('qss_load_password_screen_message_02')}
				</CustomText>
				<CustomText style={{ ...styles.subtitle1, color: theme.label_alternative }}>
					{t('qss_load_password_screen_message_03')}
				</CustomText>
				<CustomText style={{ ...styles.subtitle1, color: theme.label_alternative }}>
					{t('qss_load_password_screen_message_04')}
				</CustomText>
			</div>
			<div>
				<div style={styles.pinContainer}>
					{Array.from({ length: 6 }).map((_, index) => {
						const baseStyle: React.CSSProperties = {
							...styles.pinDot,
						};

						const activeStyle: React.CSSProperties =
							(isConfirming ? confirmPin.length > index : pin.length > index)
								? { backgroundColor: theme.primary_normal }
								: {};

						const errorStyle: React.CSSProperties =
							error && isConfirming && confirmPin.length > index ? styles.pinDotError : {};

						return (
							<div style={{ borderBottom: `1px solid ${error?theme.status_destrective:((false === isConfirming && pin.length > index) || (true === isConfirming && confirmPin.length > index) ?theme.primary_normal:theme.line_alternative) }` }}>
								<div key={index} style={{ ...baseStyle, ...activeStyle, ...errorStyle }} />
							</div>
						);
					})}
				</div>
				{
					error
						?
						<CustomText style={{ ...styles.errorText, color: theme.status_destrective }}>
							{t('create_wallet_message_07')}
						</CustomText>
						:
						<CustomText style={{ ...styles.basicText, color: theme.label_normal }}>
							{t('create_wallet_message_14')}<br />
							{/* {t('create_wallet_message_15')} */}
						</CustomText>
				}
			</div>
			<div style={styles.keypadContainer}>
				{Array.from({ length: 3 }).map((_, rowIndex) => (
					<div key={rowIndex} style={styles.keypadRow}>
						{shuffledKeys.slice(rowIndex * 3, rowIndex * 3 + 3).map((key, index) => renderKey(key, index))}
					</div>
				))}
				<div style={styles.keypadRow}>
					<div style={styles.keyPlaceholder}></div>
					{renderKey(shuffledKeys[9], 9)}
					<div key="backspace">
						<div
							onClick={() => { handleKeyPress('backspace') }}
							style={styles.key}
						>
							<Delete size={24} color={theme.label_strong} />
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}

export default QssLoadPassword
